import {
  compareHuge,
  deserializeHuge,
  hugeZero,
  isNonNegativeHuge,
  type HugeNumber,
} from "@/shared/numbers";
import type {
  NHovBranchExperienceByBranch,
  NHovBranchId,
  NHovBranchLevels,
  NHovContinuousEffectProgressByBranch,
  NHovEffectBoundaryCaps,
} from "../types";

export const NHOV_BRANCH_IDS = [
  "hgAmplify",
  "hgDiscount",
  "hgNHovEffectsToHng",
  "hngScale",
] as const satisfies readonly NHovBranchId[];

type LegacyNHovBranchId = "cellScale" | "cellChain";

type NHovBranchExperienceInput = Readonly<
  Partial<Record<NHovBranchId | LegacyNHovBranchId, unknown>>
>;

type NHovBranchEffectOverrideInput = Readonly<
  Partial<Record<NHovBranchId, unknown>>
>;

export type LegacyNHovBranchEffectOverrides = Readonly<
  Partial<Record<NHovBranchId, HugeNumber>>
>;

export function isNHovBranchId(value: unknown): value is NHovBranchId {
  return (
    typeof value === "string" &&
    (NHOV_BRANCH_IDS as readonly string[]).includes(value)
  );
}

export function createDefaultNHovBranchLevels(): NHovBranchLevels {
  return {
    hgAmplify: 0,
    hgDiscount: 0,
    hngScale: 0,
    hgNHovEffectsToHng: 0,
  };
}

export function createDefaultNHovBranchExperienceByBranch(): NHovBranchExperienceByBranch {
  return {
    hgAmplify: hugeZero(),
    hgDiscount: hugeZero(),
    hngScale: hugeZero(),
    hgNHovEffectsToHng: hugeZero(),
  };
}

export function createDefaultNHovEffectBoundaryCaps(): NHovEffectBoundaryCaps {
  return {
    hgDiscountCostDivisor: false,
  };
}

export function normalizeNHovEffectBoundaryCaps(
  value: unknown,
): NHovEffectBoundaryCaps {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return createDefaultNHovEffectBoundaryCaps();
  }
  const input = value as Partial<Record<keyof NHovEffectBoundaryCaps, unknown>>;
  return {
    hgDiscountCostDivisor: input.hgDiscountCostDivisor === true,
  };
}

function coerceNonNegativeHugeOrZero(value: unknown) {
  const normalized = deserializeHuge(value);
  return isNonNegativeHuge(normalized) ? normalized : hugeZero();
}

export function normalizeNHovBranchExperienceByBranch(
  value: NHovBranchExperienceInput | undefined,
): NHovBranchExperienceByBranch {
  if (value === undefined || value === null) {
    return createDefaultNHovBranchExperienceByBranch();
  }
  return {
    hgAmplify: coerceNonNegativeHugeOrZero(value.hgAmplify),
    hgDiscount: coerceNonNegativeHugeOrZero(value.hgDiscount),
    hngScale: coerceNonNegativeHugeOrZero(value.hngScale ?? value.cellScale),
    hgNHovEffectsToHng: coerceNonNegativeHugeOrZero(
      value.hgNHovEffectsToHng ?? value.cellChain,
    ),
  };
}

export function normalizeLegacyNHovBranchEffectOverrides(
  value: NHovBranchEffectOverrideInput | undefined,
): LegacyNHovBranchEffectOverrides {
  if (value === undefined || value === null) return {};
  const result: Partial<Record<NHovBranchId, HugeNumber>> = {};
  for (const branchId of NHOV_BRANCH_IDS) {
    if (value[branchId] === undefined) continue;
    const effect = deserializeHuge(value[branchId]);
    if (!isNonNegativeHuge(effect)) continue;
    if (branchId !== "hgNHovEffectsToHng" && compareHuge(effect, 0) <= 0) {
      continue;
    }
    result[branchId] = effect;
  }
  return result;
}

export function normalizeNHovContinuousEffectProgressByBranch(
  value: unknown,
): NHovContinuousEffectProgressByBranch {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return {};
  }
  const input = value as Partial<Record<NHovBranchId, unknown>>;
  const result: Partial<Record<NHovBranchId, true>> = {};
  for (const branchId of NHOV_BRANCH_IDS) {
    if (input[branchId] === true) result[branchId] = true;
  }
  return result;
}

export interface IsNHovBranchAvailableOptions {
  readonly branchId: NHovBranchId;
  readonly nHovCount: number;
  readonly firstExecutionBranchSelectionUnlocked?: boolean;
}

// First NHov unlocks only hgAmplify; subsequent NHovs unlock all branches.
export function isNHovBranchAvailable(
  options: IsNHovBranchAvailableOptions,
): boolean {
  if (!Number.isFinite(options.nHovCount) || options.nHovCount <= 0) {
    if (options.firstExecutionBranchSelectionUnlocked === true) {
      return true;
    }
    return options.branchId === "hgAmplify";
  }
  return true;
}
